import { EnabledFeatureFlags, FeatureFlagsContext, EnabledFeatureFlagsWidened } from "components/providers/featureFlags/featureFlagContext";
import { FeatureFlags, featureFlags, FeatureFlagsWidened } from "components/providers/featureFlags/flags";
import { useContext } from "react";

export const useFeatureFlags = <T extends keyof FeatureFlags>(name: T): EnabledFeatureFlags[T] => {
    const [flags] = useContext(FeatureFlagsContext);
    return flags[name];
};

export const useSetFeatureFlags = () => {
    const [, setState] = useContext(FeatureFlagsContext);

    return {
        toggle: (name: keyof FeatureFlags, variant?: string) => setState(changeState("toggle", name, variant)),
        enable: (name: keyof FeatureFlags, variant?: string) => setState(changeState("enable", name, variant)),
        disable: (name: keyof FeatureFlags, variant?: string) => setState(changeState("disable", name, variant)),
    };
};

const changeState = (type: "enable" | "disable" | "toggle", name: keyof FeatureFlags, variant?: string) =>
    (prev: EnabledFeatureFlagsWidened): EnabledFeatureFlagsWidened => {
        const { type: flagType, options } = (featureFlags as FeatureFlagsWidened)[name];
        if (!options) {
            console.warn(`Feature flag ${name} not found`);
            return prev;
        };

        if (flagType === "enum") {
            if (type === "disable") {
                return {
                    ...prev,
                    [name]: undefined
                };
            }

            if (!variant) {
                console.warn(`Feature flag ${name} is not a boolean, but no variant was provided`);
                return prev;
            }

            if (variant !== undefined && !(options as string[]).includes(variant)) {
                console.warn(`Feature flag ${name} has no variant ${variant === "" ? "<empty string>" : variant} (options: ${options.join(", ")})`);
                return prev;
            }

            if (type === "enable") {
                return {
                    ...prev,
                    [name]: variant
                };
            }

            return {
                ...prev,
                [name]: prev[name] === variant ? undefined : variant
            };
        }

        return {
            ...prev,
            [name]: {
                enable: true,
                disable: false,
                toggle: !prev[name]
            }[type]
        };
    };
